import React, { useState, useEffect } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { Loader } from '@googlemaps/js-api-loader';
import { AppModal } from './AppModal';
import { Field, TextInput, Select, Autocomplete } from './onyx/Field';
import { Button } from './onyx/Button';
import './profile-item-modal.css';

/**
 * Address autocomplete — Onyx Field + native onyx-input (no MUI floating label).
 */
const AddressAutocompleteForm = ({ formData, setFormData }) => {
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['address']
    },
    debounce: 300,
  });

  useEffect(() => {
    if (formData.street1) {
      setValue(formData.street1, false);
    }
  // Sync when modal opens with existing street1; setValue is stable from the hook.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.street1]);

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      await getLatLng(results[0]);
      const addressComponents = results[0].address_components;
      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let zipCode = '';

      addressComponents.forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
        if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      });

      const fullAddress = `${streetNumber} ${route}`.trim();
      setFormData(prev => ({
        ...prev,
        street1: fullAddress,
        city: city,
        state: state,
        zipCode: zipCode
      }));
      setValue(fullAddress, false);
    } catch (error) {
      console.error('Error getting geocode:', error);
    }
  };

  return (
    <div className="profile-item-fields" data-testid="profile-item-address-fields">
      <Field label="Address Type" htmlFor="profile-address-type">
        <Select
          id="profile-address-type"
          value={formData.type}
          onChange={e => setFormData({ ...formData, type: e.target.value })}
        >
          <option value="home">Home</option>
          <option value="work">Work</option>
          <option value="mailing">Mailing</option>
        </Select>
      </Field>

      <Field
        label="Street Address"
        htmlFor="profile-street1"
        required
        help="Start typing to see address suggestions"
      >
        <Autocomplete
          className="profile-item-address-autocomplete"
          options={data}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option?.description || '';
          }}
          filterOptions={(x) => x}
          freeSolo
          value={formData.street1 || ''}
          onChange={(_event, newValue) => {
            if (newValue && typeof newValue === 'object') {
              handleSelect(newValue.description);
            } else if (typeof newValue === 'string') {
              setFormData(prev => ({ ...prev, street1: newValue }));
            }
          }}
          onInputChange={(_event, newInputValue) => {
            setValue(newInputValue);
            setFormData(prev => ({ ...prev, street1: newInputValue }));
          }}
          renderInput={(params) => (
            <div
              ref={params.InputProps.ref}
              className="profile-item-address-autocomplete__control"
            >
              <input
                {...params.inputProps}
                id="profile-street1"
                className="onyx-input"
                disabled={!ready}
                required
                placeholder="Start typing your address…"
                autoComplete="street-address"
              />
              {params.InputProps.endAdornment}
            </div>
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.place_id}>
              {option.description}
            </li>
          )}
          noOptionsText={
            status === 'OK' ? 'No addresses found' : 'Start typing to search…'
          }
          slotProps={{
            paper: { className: 'profile-item-address-autocomplete__paper' },
          }}
        />
      </Field>

      <Field label="Apt / Suite (Optional)" htmlFor="profile-street2">
        <TextInput
          id="profile-street2"
          value={formData.street2}
          onChange={e => setFormData({ ...formData, street2: e.target.value })}
          placeholder="Apt, Suite, etc."
          autoComplete="address-line2"
        />
      </Field>

      <div className="profile-item-address-row">
        <Field label="City" htmlFor="profile-city" required>
          <TextInput
            id="profile-city"
            value={formData.city}
            onChange={e => setFormData({ ...formData, city: e.target.value })}
            required
            autoComplete="address-level2"
          />
        </Field>
        <Field label="State" htmlFor="profile-state" required>
          <TextInput
            id="profile-state"
            value={formData.state}
            onChange={e => setFormData({ ...formData, state: e.target.value })}
            required
            autoComplete="address-level1"
          />
        </Field>
        <Field label="ZIP Code" htmlFor="profile-zip" required>
          <TextInput
            id="profile-zip"
            value={formData.zipCode}
            onChange={e => setFormData({ ...formData, zipCode: e.target.value })}
            required
            autoComplete="postal-code"
          />
        </Field>
      </div>
    </div>
  );
};

export const ProfileItemModal = ({ isOpen, onClose, onSave, type, initialData = null }) => {
  const getInitialFormData = () => {
    const templates = {
      phone: { type: 'mobile', number: '' },
      address: { type: 'home', street1: '', street2: '', city: '', state: '', zipCode: '' },
      alias: { firstName: '', lastName: '', type: 'unofficial', prefix: '', middleNames: '', suffix: '' },
      name: { firstName: '', lastName: '' }
    };
    return initialData || templates[type] || templates.phone;
  };

  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places']
    });

    loader.load().catch(error => {
      console.error('Error loading Google Maps:', error);
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      const templates = {
        phone: { type: 'mobile', number: '' },
        address: { type: 'home', street1: '', street2: '', city: '', state: '', zipCode: '' },
        alias: { firstName: '', lastName: '', type: 'unofficial', prefix: '', middleNames: '', suffix: '' },
        name: { firstName: '', lastName: '' }
      };
      setFormData(initialData || templates[type] || templates.phone);
    }
  }, [type, isOpen, initialData]);

  const renderForm = () => {
    switch (type) {
      case 'phone':
        return (
          <div className="profile-item-fields" data-testid="profile-item-phone-fields">
            <Field label="Phone Type" htmlFor="profile-phone-type">
              <Select
                id="profile-phone-type"
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="mobile">Mobile</option>
                <option value="home">Home</option>
                <option value="work">Work</option>
              </Select>
            </Field>
            <Field label="Phone Number" htmlFor="profile-phone-number" required>
              <TextInput
                id="profile-phone-number"
                type="tel"
                value={formData.number}
                onChange={e => setFormData({ ...formData, number: e.target.value })}
                placeholder="(555) 555-5555"
                required
                autoComplete="tel"
              />
            </Field>
          </div>
        );
      case 'address':
        return <AddressAutocompleteForm formData={formData} setFormData={setFormData} />;
      case 'alias':
        return (
          <div className="profile-item-fields" data-testid="profile-item-alias-fields">
            <Field label="Alias Type" htmlFor="profile-alias-type">
              <Select
                id="profile-alias-type"
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="unofficial">Unofficial</option>
                <option value="usual">Usual</option>
                <option value="official">Official</option>
                <option value="temp">Temp</option>
                <option value="nickname">Nickname</option>
                <option value="anonymous">Anonymous</option>
                <option value="old">Old</option>
                <option value="maiden">Maiden</option>
              </Select>
            </Field>
            <Field label="Prefix" htmlFor="profile-alias-prefix">
              <TextInput
                id="profile-alias-prefix"
                value={formData.prefix}
                onChange={e => setFormData({ ...formData, prefix: e.target.value })}
                placeholder="Prefix"
              />
            </Field>
            <Field label="First Name" htmlFor="profile-alias-first" required>
              <TextInput
                id="profile-alias-first"
                value={formData.firstName}
                onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="First Name"
                required
              />
            </Field>
            <Field label="Middle Names" htmlFor="profile-alias-middle">
              <TextInput
                id="profile-alias-middle"
                value={formData.middleNames}
                onChange={e => setFormData({ ...formData, middleNames: e.target.value })}
                placeholder="Middle Names"
              />
            </Field>
            <Field label="Last Name" htmlFor="profile-alias-last" required>
              <TextInput
                id="profile-alias-last"
                value={formData.lastName}
                onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Last Name"
                required
              />
            </Field>
            <Field label="Suffix" htmlFor="profile-alias-suffix">
              <TextInput
                id="profile-alias-suffix"
                value={formData.suffix}
                onChange={e => setFormData({ ...formData, suffix: e.target.value })}
                placeholder="Suffix"
              />
            </Field>
          </div>
        );
      case 'name':
        return (
          <div className="profile-item-fields" data-testid="profile-item-name-fields">
            <Field label="First Name" htmlFor="profile-name-first" required>
              <TextInput
                id="profile-name-first"
                value={formData.firstName}
                onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="First Name"
                required
              />
            </Field>
            <Field label="Last Name" htmlFor="profile-name-last" required>
              <TextInput
                id="profile-name-last"
                value={formData.lastName}
                onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Last Name"
                required
              />
            </Field>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AppModal
      open={isOpen}
      onClose={onClose}
      title={type ? `Add ${type.charAt(0).toUpperCase() + type.slice(1)}` : ''}
      maxWidth="sm"
      className="profile-modal"
      showCloseButton={false}
      actions={
        <>
          <Button type="button" variant="secondary" size="dense" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="profile-item-form" variant="primary" size="dense">
            Save
          </Button>
        </>
      }
    >
      <form
        id="profile-item-form"
        onSubmit={e => {
          e.preventDefault();
          onSave(formData);
        }}
      >
        {renderForm()}
      </form>
    </AppModal>
  );
};
